import { useState } from "preact/hooks"
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts"
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../components/ui/table"
import { Button } from "../../../components/ui/button"
import { StatCard } from "../../../components/StatCard"
import { fmt_change, fmt_money, fmt_qty, month_label, num } from "./format"
import type { DistributionView, Placement } from "./types"

function window_label(months: string[]): string {
  const sorted = [...months].sort()
  return sorted.length ? `${month_label(sorted[0])} – ${month_label(sorted[sorted.length - 1])}` : ""
}

function StoresByMonth({ data }: { data: DistributionView }) {
  // A month after the comparison's reference month is still filling in.
  const reference = data.windows?.reference_month ?? ""
  const points = data.months.map((m: DistributionView["months"][number]) => ({
    month: m.month > reference ? `${month_label(m.month)} (to date)` : month_label(m.month),
    stores: m.stores,
    pods: m.pods,
    cost: num(m.cost),
  }))
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Stores receiving each month</CardTitle>
      </CardHeader>
      <CardContent className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={points}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" fontSize={12} />
            <YAxis yAxisId="count" fontSize={12} width={60} />
            <YAxis yAxisId="money" orientation="right" fontSize={12} width={80} tickFormatter={(v: number) => fmt_money(v)} />
            <Tooltip formatter={(v: number, name: string) => (name === "Shipped (cost)" ? fmt_money(v) : fmt_qty(v))} />
            <Legend />
            <Bar yAxisId="money" dataKey="cost" name="Shipped (cost)" fill="#bfdbfe" />
            <Line yAxisId="count" type="monotone" dataKey="stores" name="Stores" stroke="#2563eb" strokeWidth={2} />
            <Line yAxisId="count" type="monotone" dataKey="pods" name="Store × item placements" stroke="#16a34a" strokeWidth={2} />
          </ComposedChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  )
}

function PlacementTable({ rows, empty }: { rows: Placement[]; empty: string }) {
  if (!rows.length) return <p class="p-4 text-sm text-gray-600">{empty}</p>
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Store</TableHead>
          <TableHead>Retailer</TableHead>
          <TableHead>City</TableHead>
          <TableHead>Item</TableHead>
          <TableHead>Last received</TableHead>
          <TableHead className="text-right">Units</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((p: Placement) => (
          <TableRow key={`${p.store_number}-${p.upc}`}>
            <TableCell>
              {p.store_name} <span class="text-xs text-gray-500">#{p.store_number}</span>
            </TableCell>
            <TableCell>{p.retailer_name ?? p.retailer_parent ?? "—"}</TableCell>
            <TableCell>{[p.city, p.state].filter(Boolean).join(", ") || "—"}</TableCell>
            <TableCell>{p.item_name}</TableCell>
            <TableCell>{month_label(p.last_month)}</TableCell>
            <TableCell className="text-right">{fmt_qty(p.qty)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

export function DistributionTab({ data, className }: { data: DistributionView; className?: string }) {
  const [list, set_list] = useState<"lost" | "new">("lost")
  if (!data.windows) {
    return <p class="text-sm text-gray-600">No KeHE store volume pulled yet. It arrives with the daily KeHE reports sync.</p>
  }
  const recent = window_label(data.windows.recent)
  const prior = window_label(data.windows.prior)
  const stores_recent = data.retailers.reduce((n: number, r: DistributionView["retailers"][number]) => n + r.stores_recent, 0)
  const stores_prior = data.retailers.reduce((n: number, r: DistributionView["retailers"][number]) => n + r.stores_prior, 0)
  const lost_total = data.retailers.reduce((n: number, r: DistributionView["retailers"][number]) => n + r.lost, 0)
  const new_total = data.retailers.reduce((n: number, r: DistributionView["retailers"][number]) => n + r.new, 0)
  return (
    <div class={`space-y-4 ${className ?? ""}`}>
      <p class="text-sm text-gray-600">
        Compares <strong>{recent}</strong> with the three months before (<strong>{prior}</strong>). A placement is lost when a store
        received the item before and nothing since; new when it received it only lately. The current month, still filling in, is
        left out.
      </p>
      <div class="grid gap-4 md:grid-cols-4">
        <StatCard title="Stores receiving" value={fmt_qty(stores_recent)} detail={`${fmt_change(stores_recent, stores_prior)} vs ${prior}`} />
        <StatCard title="Lost placements" value={fmt_qty(lost_total)} detail="store × item, nothing received lately" warn={lost_total > 0} />
        <StatCard title="New placements" value={fmt_qty(new_total)} detail="store × item, first received lately" />
        <StatCard title="Retailers" value={fmt_qty(data.retailers.length)} detail="parents with stores receiving" />
      </div>
      <StoresByMonth data={data} />
      <div class="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">By retailer</CardTitle>
          </CardHeader>
          <CardContent className="max-h-[420px] overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Retailer parent</TableHead>
                  <TableHead className="text-right">Stores</TableHead>
                  <TableHead className="text-right">Change</TableHead>
                  <TableHead className="text-right">Shipped (cost)</TableHead>
                  <TableHead className="text-right">Lost</TableHead>
                  <TableHead className="text-right">New</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.retailers.map((r: DistributionView["retailers"][number]) => (
                  <TableRow key={r.retailer_parent}>
                    <TableCell>{r.retailer_parent}</TableCell>
                    <TableCell className="text-right">{fmt_qty(r.stores_recent)}</TableCell>
                    <TableCell className="text-right">{fmt_change(r.stores_recent, r.stores_prior)}</TableCell>
                    <TableCell className="text-right">{fmt_money(r.cost_recent)}</TableCell>
                    <TableCell className="text-right">{r.lost || "—"}</TableCell>
                    <TableCell className="text-right">{r.new || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">By item</CardTitle>
          </CardHeader>
          <CardContent className="max-h-[420px] overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Item</TableHead>
                  <TableHead className="text-right">Stores</TableHead>
                  <TableHead className="text-right">Change</TableHead>
                  <TableHead className="text-right">Units</TableHead>
                  <TableHead className="text-right">Lost</TableHead>
                  <TableHead className="text-right">New</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((i: DistributionView["items"][number]) => (
                  <TableRow key={i.upc}>
                    <TableCell>{i.item_name}</TableCell>
                    <TableCell className="text-right">{fmt_qty(i.stores_recent)}</TableCell>
                    <TableCell className="text-right">{fmt_change(i.stores_recent, i.stores_prior)}</TableCell>
                    <TableCell className="text-right">{fmt_qty(i.qty_recent)}</TableCell>
                    <TableCell className="text-right">{i.lost || "—"}</TableCell>
                    <TableCell className="text-right">{i.new || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-base">{list === "lost" ? "Lost placements" : "New placements"}</CardTitle>
          <div class="flex gap-1">
            <Button size="sm" variant={list === "lost" ? "default" : "outline"} onClick={() => set_list("lost")}>
              Lost ({fmt_qty(lost_total)})
            </Button>
            <Button size="sm" variant={list === "new" ? "default" : "outline"} onClick={() => set_list("new")}>
              New ({fmt_qty(new_total)})
            </Button>
          </div>
        </CardHeader>
        <CardContent className="max-h-[520px] overflow-auto">
          <PlacementTable
            rows={list === "lost" ? data.lost : data.new}
            empty={list === "lost" ? "No lost placements." : "No new placements."}
          />
        </CardContent>
      </Card>
    </div>
  )
}
